/**
 * EDITOR-6558: Denylist of toolbar component `key` strings that should be
 * hidden when the editor is in Markdown Mode.
 *
 * The strings here come from `@atlaskit/editor-common/toolbar/keys`, where
 * each plugin's toolbar registration uses a stable key constant. Anything
 * matching one of these keys is filtered out by `toolbarPlugin`'s
 * `componentFilter` before it reaches the registry, so the buttons never
 * appear (regardless of whether the toolbar is pinned or floating).
 *
 * The list is the toolbar-component-flavoured equivalent of
 * `MARKDOWN_COMPATIBLE_TOOLBAR_ITEM_NAMES` (which targets the `+` insert
 * block specifically). Entries are conservative — only marks/nodes with no
 * GFM round-trip are excluded. Marks like `bold` / `italic` / `code` /
 * `strike` are intentionally NOT in the list because they have a clean GFM
 * representation.
 *
 * This is a denylist (not an allowlist) because toolbar components are
 * registered by ~17 different plugins with hundreds of stable keys; an
 * allowlist would silently break unrelated functionality (selection
 * toolbar, table toolbar, AI floating toolbar, etc.) the moment we miss
 * a key.
 */
export const MARKDOWN_INCOMPATIBLE_TOOLBAR_COMPONENT_KEYS: readonly string[] = [
	// --- Visual marks with no GFM equivalent ---
	// Text colour & highlight (lossy on round-trip)
	'text-color-highlight-group',
	'text-color-highlight-menu',
	'text-color-highlight-menu-section',
	'text-color-menu-item',
	'highlight-menu-item',

	// Underline / sub / super (GFM strict mode doesn't include these)
	'underline-button-group',
	'underline-button',
	'underline-menu-item',
	'subscript-menu-item',
	'superscript-menu-item',

	// Alignment (GFM has no inline alignment marker)
	'alignment-group',
	'alignment-menu',
	'alignment-menu-section',

	// Indentation as a mark (lists themselves are still supported)
	// Note: list buttons themselves remain — only the indentation-as-mark
	// menu item is removed. We keep the broader lists-indentation group
	// because it carries the bulk of list functionality.

	// --- Inline / block nodes with no GFM equivalent ---
	'mention-group',
	'mention-button',
	'emoji-group',
	'emoji-button',
	'layout-group',
	'layout-button',

	// Rich media — Atlassian-cloud media (collection IDs, layouts, captions,
	// dimensions) doesn't survive markdown round-trip. Plain image upload
	// is still available via the `+` insert block (`![alt](src)`).
	'media-group',
	'media-button',

	// Synced blocks — Atlassian-cloud-only block reuse mechanism with no
	// representation in markdown.
	'synced-block-group',
	'synced-block-button',
	'synced-block-section',
];

export const MARKDOWN_INCOMPATIBLE_TOOLBAR_COMPONENT_KEYS_SET: ReadonlySet<string> = new Set(
	MARKDOWN_INCOMPATIBLE_TOOLBAR_COMPONENT_KEYS,
);

/**
 * Predicate suitable for passing as `ToolbarPluginOptions.componentFilter`
 * when in Markdown Mode. Returns `false` for components on the denylist
 * (silently dropped from the toolbar) and `true` for everything else.
 */
export const isMarkdownCompatibleToolbarComponent = (component: {
	key: string;
	type: string;
}): boolean => {
	return !MARKDOWN_INCOMPATIBLE_TOOLBAR_COMPONENT_KEYS_SET.has(component.key);
};
